import { useState } from "react"
import { AdminMenuSettingPopup } from "../components/adminmenu/AdminMenuSettingPopup"
import { TreeMenu } from "../components/adminmenu/TreeMenu"
import { Button } from "../components/tag/Button"
import { ButtonWrap, DefDiv, PageTitle, SectionTitle, InfoText, FormField } from "../components/tag/CustomTag"
import { Input } from "../components/tag/Input"
import { Switch } from "../components/tag/Switch"
import { usePopup } from "../components/utils/PopupProvider"
import { setStateSO, stateInvalidCheck } from "../components/utils/State"
import { showConfirm } from "../components/utils/Confirm"
import { showToast } from "../components/utils/Toastify"
import { useGraphqlService } from "../components/service/GraphqlService"

const DEFAULT_FORM = {
    NO: null,
    MENU_NAME: "",
    MENU_DESC: "",
    PARENT_MENU: { NO: null, MENU_NAME: "" },
    MENU_URL: "",
    MENU_ICON: "",
    MENU_ORDER: 1,
    MENU_ENABLE: 1,
    MENU_DISPLAY: 1,
    MENU_LOGIN_CHECK: 1,
};

export const AdminMenuSettingPage = () => {
    const { executeMutation, objToQueryData } = useGraphqlService();
    const { showPopup, hidePopup } = usePopup();
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);

    const handleSelectMenu = (menu, parentMenu) => {
        setSelectedMenu(menu);
        setFormData({
            ...DEFAULT_FORM,
            NO: menu.NO,
            MENU_NAME: menu.MENU_NAME,
            PARENT_MENU: parentMenu ?? { NO: null, MENU_NAME: "" },
        });
    };

    const openPopup = () => {
        const id = showPopup(<AdminMenuSettingPopup selectedParentMenu={formData.PARENT_MENU}
            onSelectParentMenu={(menu) => setStateSO(setFormData, 'PARENT_MENU', menu)}
            onClose={() => hidePopup(id)} />, { className: "max-w-[500px]", closeOnOverlayClick: true });
    }

    const handleSaveMenu = async () => {
        if (stateInvalidCheck(formData, ['MENU_NAME'])) {
            await showToast.error("필수 항목을 입력해주세요.");
            return;
        }

        const { PARENT_MENU, ...rest } = formData;
        const reqData = {
            ...rest,
            PARENT_MENU_NO: PARENT_MENU?.NO,
        };

        try {
            const result = await executeMutation(`mutation { upsertMenu(data: {${objToQueryData(reqData)}}) }`);
            console.log(result);
            if (result) {
                showToast.success("메뉴 저장 성공");
            }
        } catch (error) {
            console.log(error);
            showToast.error("메뉴 저장 실패");
        }
    }

    const handleDeleteMenu = async () => {
        const confirm = await showConfirm({ text: '정말 삭제하시겠습니까?' });
        if (!confirm) return;
        console.log("삭제이벤트")
    }

    return (
        <div className="flex flex-col h-full gap-6">
            <PageTitle title={"메뉴 설정"} />
            <div className="flex flex-col lg:flex-row gap-4 flex-1">
                <DefDiv className="max-w-full lg:max-w-[355px] flex flex-col gap-3">
                    <SectionTitle>메뉴 구조</SectionTitle>
                    <TreeMenu
                        theme={"white"}
                        selectParentMenu={selectedMenu}
                        onSelectParentMenu={handleSelectMenu}
                    />
                </DefDiv>

                <DefDiv className="flex flex-col">
                    <div className="flex-1 flex flex-col gap-3">
                        <SectionTitle>메뉴 정보</SectionTitle>
                        <div className="flex flex-wrap gap-4">
                            <FormField label="메뉴명" className="flex-1 basis-[calc(50%-8px)] min-w-[280px]">
                                <Input
                                    value={formData.MENU_NAME}
                                    onChange={(e) => setStateSO(setFormData, 'MENU_NAME', e.target.value)}
                                    type="text" name="MENU_NAME" id="MENU_NAME" className="bg-white rounded" />
                            </FormField>
                            <FormField label="메뉴 설명" className="flex-1 basis-[calc(50%-8px)] min-w-[280px]">
                                <Input
                                    value={formData.MENU_DESC}
                                    onChange={(e) => setStateSO(setFormData, 'MENU_DESC', e.target.value)}
                                    type="text" name="MENU_DESC" id="MENU_DESC" className="bg-white rounded" />
                            </FormField>
                            <FormField label="상위 메뉴" className="flex-1 basis-[calc(50%-8px)] min-w-[280px]">
                                <div className="flex gap-2">
                                    <Input
                                        value={formData.PARENT_MENU?.MENU_NAME || ""}
                                        type="text" readOnly name="PARENT_MENU" id="PARENT_MENU" className="bg-white rounded" />
                                    <Button onClick={openPopup} className="btn-primary w-[80px] rounded font-bold text-sm">선택</Button>
                                </div>
                            </FormField>
                            <FormField label="메뉴 경로" className="flex-1 basis-[calc(50%-8px)] min-w-[280px]">
                                <Input
                                    value={formData.MENU_URL || "/"}
                                    onChange={(e) => setStateSO(setFormData, 'MENU_URL', e.target.value)}
                                    type="text" name="MENU_URL" id="MENU_URL" className="bg-white rounded" />
                            </FormField>
                            <FormField label="메뉴 Icon" className="flex-1 basis-[calc(50%-8px)] min-w-[280px]">
                                <Input
                                    value={formData.MENU_ICON}
                                    onChange={(e) => setStateSO(setFormData, 'MENU_ICON', e.target.value)}
                                    type="text" name="MENU_ICON" id="MENU_ICON" className="bg-white rounded" />
                            </FormField>
                            <FormField label="정렬 순서" className="flex-1 basis-[calc(50%-8px)] min-w-[280px]">
                                <Input
                                    value={formData.MENU_ORDER}
                                    onChange={(e) => setStateSO(setFormData, 'MENU_ORDER', e.target.value)}
                                    type="number" name="MENU_ORDER" id="MENU_ORDER" className="bg-white rounded" />
                            </FormField>
                        </div>
                        <SectionTitle>메뉴 옵션</SectionTitle>
                        <div className="flex flex-col gap-3">
                            <div className="flex justify-between items-center lg:w-[calc(50%-8px)]">
                                <div>
                                    <p className="text-lg text-white">메뉴 활성화</p>
                                    <InfoText>비활성화 시 모든 계정에서 숨겨집니다.</InfoText>
                                </div>
                                <Switch key={`enable-${selectedMenu?.NO}-${formData.MENU_ENABLE}`} enabled={formData.MENU_ENABLE}
                                    onChange={(val) => setStateSO(setFormData, 'MENU_ENABLE', val || 0)} />
                            </div>
                            <div className="flex justify-between items-center lg:w-[calc(50%-8px)]">
                                <div>
                                    <p className="text-lg text-white">사이드바 표시</p>
                                    <InfoText>좌측 네비게이션에 노출 여부</InfoText>
                                </div>
                                <Switch key={`display-${selectedMenu?.NO}-${formData.MENU_DISPLAY}`} enabled={formData.MENU_DISPLAY}
                                    onChange={(val) => setStateSO(setFormData, 'MENU_DISPLAY', val || 0)} />
                            </div>
                            <div className="flex justify-between items-center lg:w-[calc(50%-8px)]">
                                <div>
                                    <p className="text-lg text-white">로그인 필요</p>
                                    <InfoText>비인증 사용자 접근 차단</InfoText>
                                </div>
                                <Switch key={`login-${selectedMenu?.NO}-${formData.MENU_LOGIN_CHECK}`} enabled={formData.MENU_LOGIN_CHECK}
                                    onChange={(val) => setStateSO(setFormData, 'MENU_LOGIN_CHECK', val || 0)} />
                            </div>
                        </div>
                    </div>
                    <ButtonWrap>
                        <Button onClick={() => setFormData(DEFAULT_FORM)} className="w-20 h-8 bg-[#0092B8] rounded text-white font-bold text-[0.9rem]">
                            추가
                        </Button>
                        <Button onClick={handleSaveMenu} className="w-20 h-8 bg-[#0092B8] rounded text-white font-bold text-[0.9rem]">
                            저장
                        </Button>
                        {formData.NO &&
                            <Button onClick={handleDeleteMenu} className="w-20 h-8 bg-[#F54900] rounded text-white font-bold text-[0.9rem]">
                                삭제
                            </Button>
                        }
                    </ButtonWrap>
                </DefDiv>
            </div>
        </div>
    )
}